import {Card, CardHeader, CardBody, CardTitle, Button, Row, Col} from "reactstrap";
import {useUser} from "../../contexts/AuthContext";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import {formatTime, formatDate} from "../../utils/functions";
import HelpIcon from "../Help/HelpIcon";

import images from "../../styles/images.module.css";
import structure from "../../styles/structure.module.css";

const order = (a: any, b: any) => (a.channelId < b.channelId ? -1 : a.channelId > b.channelId ? 1 : 0);

const CompactSessionReport = (props: any) => {
  const {user} = useUser();
  const timezoneOffsetMinutes: number = user.settings.timezoneOffsetMinutes;

  // Type assertions for CSS modules
  const imagesClasses = images as unknown as {[key: string]: string};
  const structureClasses = structure as unknown as {[key: string]: string};

  const isFire = (str: string): RegExpMatchArray | null => {
    if (!str) {
      return null;
    }
    return str.match(/(.*)fire(.*)/i);
  };

  if (props === undefined || !props.startTimestampMilliseconds) {
    return <></>;
  }

  // Combine all devices into a single array with type labels
  const allDevices: any[] = [];

  if (props.antDevices?.length) {
    props.antDevices.forEach((device: any) => {
      allDevices.push({...device, type: "ANT+", badgeColor: "device-ant"});
    });
  }

  if (props.bleDevicesNative?.length) {
    props.bleDevicesNative.forEach((device: any) => {
      allDevices.push({...device, type: "BLE (native)", badgeColor: "device-ble"});
    });
  }

  if (props.bleDevicesCompanion?.length) {
    props.bleDevicesCompanion.forEach((device: any) => {
      allDevices.push({...device, type: "BLE (companion)", badgeColor: "device-ble"});
    });
  }

  if (props.bleDevicesLan?.length) {
    props.bleDevicesLan.forEach((device: any) => {
      allDevices.push({...device, type: "BLE (LAN)", badgeColor: "device-ble"});
    });
  }

  if (props.zpDevices?.length) {
    props.zpDevices.forEach((device: any) => {
      allDevices.push({...device, type: "WiFi", badgeColor: "device-wifi"});
    });
  }

  const sortedDevices = allDevices.sort(order);

  let platformClass: string | undefined = undefined;
  let cpuClass: string | undefined = undefined;
  let gpuClass: string | undefined = undefined;

  if (props.platformName) {
    switch (props.platformName.toLowerCase()) {
      case "alienware":
        platformClass = imagesClasses["alienware"];
        break;

      case "pc":
        platformClass = imagesClasses["pc"];
        break;

      case "mac":
        platformClass = imagesClasses["mac"];
        break;

      case "ios":
        platformClass = imagesClasses["mac"];
        break;

      case "android":
        platformClass = imagesClasses["android"];
        break;

      default:
        platformClass = undefined;
        break;
    }
  }

  if (props.cpuVendor) {
    switch (props.cpuVendor.toLowerCase()) {
      case "amd":
        cpuClass = imagesClasses["amd"];
        break;

      case "intel":
        cpuClass = imagesClasses["intel"];
        break;

      case "apple":
        cpuClass = imagesClasses["mac"];
        break;

      case "virtualapple":
      case "arm64":
        cpuClass = imagesClasses["mac"];
        break;

      case "qualcomm":
        cpuClass = imagesClasses["qualcomm"];
        break;

      case "arm":
        cpuClass = imagesClasses["arm"];
        break;

      default:
        cpuClass = imagesClasses["unknown"];
        break;
    }
  }

  if (props.gpuVendor) {
    switch (props.gpuVendor.toLowerCase()) {
      case "amd":
        if (isFire(props.gpuVendor)) {
          gpuClass = imagesClasses["firepro"];
        } else {
          gpuClass = imagesClasses["radeon"];
        }
        break;
      case "ati":
        if (isFire(props.gpuVendor)) {
          gpuClass = imagesClasses["firepro"];
        } else {
          gpuClass = imagesClasses["ati"];
        }
        break;

      case "nvidia":
        gpuClass = imagesClasses["nvidia"];
        break;

      case "intel":
        gpuClass = imagesClasses["intel"];
        break;

      case "apple":
      case "arm64":
        gpuClass = imagesClasses["mac"];
        break;

      case "qualcomm":
        gpuClass = imagesClasses["qualcomm"];
        break;

      case "arm":
        gpuClass = imagesClasses["arm"];
        break;

      default:
        gpuClass = undefined;
        break;
    }
  }

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <Row>
          <Col lg="12" md="12">
            <Card className="compact-session-card">
              <CardHeader data-color={color}>
                <Row>
                  <Col md="6" className="d-flex align-items-center">
                    <CardTitle tag="h4">Session & Devices</CardTitle>
                  </Col>
                  {props.isShareable === true && (
                    <Col md="6" className="d-flex justify-content-end">
                      <Button color="primary" data-color={color} size="sm" onClick={props.toggleModalShare}>
                        Share <i className="fa fa-copy" />
                      </Button>
                    </Col>
                  )}
                </Row>
              </CardHeader>
              <CardBody className="compact-session-body">
                {/* Session Metrics in Horizontal Layout */}
                <div className="session-metrics-grid">
                  <div className="metric-item">
                    <span className="metric-label">Date</span>
                    <span className="metric-value">
                      {formatDate(props.startTimestampMilliseconds, timezoneOffsetMinutes)}
                    </span>
                  </div>

                  <div className="metric-item">
                    <span className="metric-label">Time</span>
                    <span className="metric-value">
                      {formatTime(props.startTimestampMilliseconds, timezoneOffsetMinutes)}
                    </span>
                  </div>

                  <div className="metric-item">
                    <span className="metric-label">
                      Duration
                      <HelpIcon topicId="sessionDuration" size="sm" placement="inline" />
                    </span>
                    <span className="metric-value">{props.humanizedDuration}</span>
                  </div>

                  <div className="metric-item">
                    <span className="metric-label">
                      Activities
                      <HelpIcon topicId="activitiesCount" size="sm" placement="inline" />
                    </span>
                    <span className="metric-value">{props.activitiesCount}</span>
                  </div>

                  <div className="metric-item">
                    <span className="metric-label">
                      Load Time
                      <HelpIcon topicId="loadTime" size="sm" placement="inline" />
                    </span>
                    <span className="metric-value">{props.loadingTime}</span>
                  </div>

                  <div className="metric-item">
                    <span className="metric-label">
                      Game Version
                      <HelpIcon topicId="gameVersion" size="sm" placement="inline" />
                    </span>
                    <span className="metric-value">{props.gameVersion}</span>
                  </div>

                  <div className="metric-item">
                    <span className="metric-label">
                      Launcher
                      <HelpIcon topicId="launcherVersion" size="sm" placement="inline" />
                    </span>
                    <span className="metric-value">{props.launcherVersion}</span>
                  </div>

                  <div className="metric-item">
                    <span className="metric-label">
                      Video Screenshots
                      <HelpIcon topicId="videoScreenshots" size="sm" placement="inline" />
                    </span>
                    <span className={`metric-value ${props.isVideoCaptureEnabled ? "text-danger" : "text-success"}`}>
                      {props.isVideoCaptureEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>

                {/* System Section */}
                <>
                  <div className="devices-divider"></div>
                  <div className="devices-section">
                    {/* <h6 className="devices-title">System</h6> */}
                    <div className="devices-grid">
                      <div key="devices-system" className="device-item">
                        <div>
                          <span className="device-profile">System</span>
                        </div>
                        <div className={structureClasses["imageFloat"]}>
                          <div className={structureClasses["imageWrapperOuter"]}>
                            <div className={structureClasses["imageWrapperInner"]}>
                              <div className={platformClass} data-label={props.platform}></div>
                            </div>
                          </div>
                        </div>
                        <div className={structureClasses["imageFloat"]}>
                          <div className={structureClasses["imageWrapperOuter"]}>
                            <div className={structureClasses["imageWrapperInner"]}>
                              <div className={cpuClass} data-label={props.cpuName}></div>
                            </div>
                          </div>
                        </div>
                        <div className={structureClasses["imageFloat"]}>
                          <div className={structureClasses["imageWrapperOuter"]}>
                            <div className={structureClasses["imageWrapperInner"]}>
                              <div className={gpuClass} data-label={props.gpuRenderer}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div key="devices-cpu" className="device-item">
                        <span className="device-profile">CPU</span>
                        <span className="device-model">
                          <a
                            href={`/search/${encodeURIComponent(`${props.cpuName} ${props.platformName || ""}`.trim())}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="search-link"
                          >
                            {props.cpuName} <i className="fa fa-search fa-xs"></i>
                          </a>
                        </span>
                      </div>
                      <div key="devices-ram" className="device-item">
                        <span className="device-profile">RAM</span>
                        <span className="device-model">{props.ram}</span>
                      </div>
                      <div key="devices-gpu" className="device-item">
                        <span className="device-profile">GPU</span>
                        <span className="device-model">
                          <a
                            href={`/search/${encodeURIComponent(`${props.gpuRenderer} ${props.platformName || ""}`.trim())}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="search-link"
                          >
                            {props.gpuRenderer} <i className="fa fa-search fa-xs"></i>
                          </a>{" "}
                          {props.gfxVendorMatchFound ? (
                            <>
                              <i className="fa fa-check-square good"></i>
                              <HelpIcon topicId="gpuProfileMatch" size="sm" placement="inline" />
                            </>
                          ) : (
                            <>
                              <i className="fa fa-poop poop"></i>
                              <HelpIcon topicId="gpuProfileMatch" size="sm" placement="inline" />
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </>

                {/* Devices Section */}
                {sortedDevices.length > 0 && (
                  <>
                    <div className="devices-divider"></div>
                    <div className="devices-section">
                      {/* <h6 className="devices-title">Connected Devices ({sortedDevices.length})</h6> */}
                      <div className="devices-grid">
                        {sortedDevices.map((device: any, index: number) => (
                          <div key={`device-${index}`} className="device-item">
                            <span className={`device-type-badge badge badge-${device.badgeColor}`}>{device.type}</span>
                            <span className="device-profile">{device.profile}</span>
                            <span className="device-model">{device.model}</span>
                            <span className="device-time">
                              {device.timestamp && formatTime(device.timestamp, timezoneOffsetMinutes)}
                            </span>
                            {device.pairingType === 0 && <span className="device-channel">Ch.{device.channelId}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default CompactSessionReport;
